import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  getUserConversations,
  getConversationMessages,
  createConversation,
  renameConversation,
  deleteConversation,
} from '../services/api';
import { getConversationIdFromPath } from '../utils/urlUtils';

/**
 * 对话管理 hook
 * 负责：对话列表、选中、CRUD、URL 同步
 */
export function useConversations(isAuthenticated, token, showSnackbar) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const location = useLocation();

  // 加载对话消息
  const loadConversationMessages = useCallback(async (convId) => {
    if (!isAuthenticated || !token) return;
    try {
      const response = await getConversationMessages(convId, token);
      return response.messages;
    } catch (error) {
      console.error('加载对话消息失败:', error);
      return [];
    }
  }, [isAuthenticated, token]);

  // 加载对话列表
  const loadConversations = useCallback(async () => {
    if (!isAuthenticated || !token) return;
    try {
      const response = await getUserConversations(token);
      const convs = response.conversations || [];
      setConversations(convs);
      return convs;
    } catch (error) {
      console.error('加载对话列表失败:', error);
      return [];
    }
  }, [isAuthenticated, token]);

  // 选择对话
  const selectConversation = useCallback((conversation) => {
    if (!conversation) return;
    const convId = String(conversation._id);
    setSelectedConversation(conversation);

    navigate(`/conversation/${convId}`);
  }, [navigate]);

  // 创建新对话
  const createNewConversation = useCallback(async () => {
    if (!isAuthenticated || !token) return null;
    try {
      const now = new Date();
      const name = `对话 ${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const response = await createConversation(token, name);
      const newConv = response.conversation;
      setConversations(prev => [newConv, ...prev]);
      selectConversation(newConv);
      return newConv;
    } catch (error) {
      console.error('创建新对话失败:', error);
      return null;
    }
  }, [isAuthenticated, token, selectConversation]);

  // 重命名
  const renameConv = useCallback(async (convId, newTitle) => {
    if (!isAuthenticated || !token) return;
    try {
      await renameConversation(convId, newTitle, token);
      setConversations(prev =>
        prev.map(c => c._id === convId ? { ...c, title: newTitle } : c)
      );
    } catch (error) {
      console.error('重命名对话失败:', error);
    }
  }, [isAuthenticated, token]);

  // 删除
  const deleteConv = useCallback(async (convToDelete) => {
    if (!isAuthenticated || !token) return;
    const convId = typeof convToDelete === 'string' ? convToDelete : convToDelete._id;
    try {
      await deleteConversation(convId, token);
      setConversations(prev => {
        const updated = prev.filter(c => c._id !== convId);
        const wasSelected = selectedConversation?._id === convId;
        if (wasSelected) {
          setSelectedConversation(null);
          if (updated.length > 0) {
            setTimeout(() => selectConversation(updated[0]), 0);
          } else {
            setTimeout(() => createNewConversation(), 0);
          }
          navigate('/');
        }
        return updated;
      });
    } catch (error) {
      console.error('删除对话失败:', error);
    }
  }, [isAuthenticated, token, selectedConversation, navigate, selectConversation, createNewConversation]);

  // 本地更新对话标题（用于socket事件同步，无需调用API）
  const updateConversationTitleLocal = useCallback((convId, newTitle) => {
    setConversations(prev =>
      prev.map(c => String(c._id) === String(convId) ? { ...c, title: newTitle, updatedAt: new Date() } : c)
    );
    // 如果当前选中的就是这个对话，也更新选中状态
    setSelectedConversation(prev => {
      if (prev && String(prev._id) === String(convId)) {
        return { ...prev, title: newTitle };
      }
      return prev;
    });
  }, []);

  // URL 同步：当 URL 中的对话 ID 变化时选中对应对话
  useEffect(() => {
    const urlConvId = conversationId || getConversationIdFromPath(location.pathname);
    if (!urlConvId || !isAuthenticated) return;
    const conv = conversations.find(c => c._id === urlConvId);
    if (conv && String(selectedConversation?._id) !== urlConvId) {
      setSelectedConversation(conv);
    }
  }, [conversationId, conversations, isAuthenticated, location.pathname, selectedConversation]);

  return {
    conversations,
    selectedConversation,
    setSelectedConversation,
    loadConversations,
    loadConversationMessages,
    selectConversation,
    createNewConversation,
    renameConv,
    deleteConv,
    updateConversationTitleLocal,
  };
}